import { useCallback, useEffect, useMemo, useState } from 'react'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { DropDownList, DropDownListFilterChangeEvent } from '@progress/kendo-react-dropdowns'
import { Input, NumericTextBox, Switch, TextArea } from '@progress/kendo-react-inputs'
import { FaPlus } from 'react-icons/fa'

import {
  UniformItem,
  UniformRequest,
  UniformStatus,
  UniformAllowanceSummary,
  createUniformRequestOnBehalf,
  deleteUniformItem,
  getEmployeeUniform,
  getUniformItems,
  getUniformRequests,
  saveUniformItem,
  updateUniformRequestStatus,
} from '../../data/uniformData'
import { getEmployeeDropdown } from '../../data/employeeData'
import { dropDown } from '../../data/interfaces'
import { useToast } from '../../contexts/ToastContext'
import { getApiErrorDetails } from '../../helpers/apiError'
import UniformAllocationsTab from './UniformAllocationsTab'
import UniformGroupsTab from './UniformGroupsTab'
import './Uniform.css'

export const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'

export const StatusPill = ({ status }: { status: UniformStatus }) => (
  <span className={`uni-pill uni-pill--${status.toLowerCase()}`}>{status}</span>
)

export const RequestLines = ({ request }: { request: UniformRequest }) => (
  <ul className='uni-lines'>
    {request.lines.map((l, i) => (
      <li key={i}>
        {l.quantity} × {l.itemName}
        {l.size ? <span className='uni-muted'> ({l.size})</span> : null}
      </li>
    ))}
  </ul>
)

// The allowance year runs 1 October to 30 September.
const allowanceYear = (now = new Date()) => {
  const startYear = now.getMonth() >= 9 ? now.getFullYear() : now.getFullYear() - 1
  return { start: new Date(startYear, 9, 1), end: new Date(startYear + 1, 8, 30) }
}

type Filter = 'Outstanding' | UniformStatus | 'All'
const FILTERS: Filter[] = ['Outstanding', 'Requested', 'Ordered', 'Delivered', 'Declined', 'Cancelled', 'All']

type Tab = 'requests' | 'allocations' | 'groups' | 'items'
const TABS: { key: Tab; label: string }[] = [
  { key: 'requests', label: 'Requests' },
  { key: 'allocations', label: 'Allocations' },
  { key: 'groups', label: 'Groups' },
  { key: 'items', label: 'Items' },
]

/**
 * Uniform, for whoever manages it: the requests coming in (marked ordered,
 * delivered, declined or cancelled here), raising one for someone, everyone's
 * allowances, which groups are offered which items, and the items themselves.
 */
const UniformPage = () => {
  const [tab, setTab] = useState<Tab>('requests')
  const [newRequestOpen, setNewRequestOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<UniformItem | 'new' | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const year = allowanceYear()

  return (
    <div className='uni-page'>
      <header className='uni-header'>
        <div className='uni-header__inner'>
          <div>
            <h1 className='uni-title'>Uniform</h1>
            <p className='uni-subtitle'>
              Allowance year {formatDate(year.start.toISOString())} to {formatDate(year.end.toISOString())}. Allowances reset every 1 October.
            </p>
            <div className='uni-tabs' role='tablist'>
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type='button'
                  role='tab'
                  aria-selected={tab === t.key}
                  className={`uni-tab${tab === t.key ? ' uni-tab--active' : ''}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ paddingBottom: '0.75rem' }}>
            {tab === 'requests' && (
              <button type='button' className='uni-btn uni-btn--accent' onClick={() => setNewRequestOpen(true)}>
                <FaPlus /> New request
              </button>
            )}
            {tab === 'items' && (
              <button type='button' className='uni-btn uni-btn--accent' onClick={() => setEditingItem('new')}>
                <FaPlus /> Add item
              </button>
            )}
          </div>
        </div>
      </header>

      <div className='uni-body'>
        {tab === 'requests' && <RequestsTab refreshKey={refreshKey} />}
        {tab === 'allocations' && <UniformAllocationsTab refreshKey={refreshKey} />}
        {tab === 'groups' && <UniformGroupsTab refreshKey={refreshKey} />}
        {tab === 'items' && <ItemsTab refreshKey={refreshKey} onEdit={setEditingItem} />}
      </div>

      {newRequestOpen && (
        <NewRequestDialog
          onClose={() => setNewRequestOpen(false)}
          onCreated={() => {
            setNewRequestOpen(false)
            setRefreshKey((k) => k + 1)
          }}
        />
      )}

      {editingItem && (
        <ItemDialog
          item={editingItem === 'new' ? null : editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={() => {
            setEditingItem(null)
            setRefreshKey((k) => k + 1)
          }}
        />
      )}
    </div>
  )
}

// --- Requests ------------------------------------------------------------------

const RequestsTab = ({ refreshKey }: { refreshKey: number }) => {
  const { showToast } = useToast()
  const [filter, setFilter] = useState<Filter>('Outstanding')
  const [search, setSearch] = useState('')
  const [requests, setRequests] = useState<UniformRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [reasonFor, setReasonFor] = useState<{ request: UniformRequest; status: 'Declined' | 'Cancelled' } | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRequests(await getUniformRequests())
    } catch (error) {
      showToast(getApiErrorDetails(error, "Uniform requests couldn't be loaded.").message, 'error')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return requests.filter((r) => {
      const statusMatch =
        filter === 'All' ||
        (filter === 'Outstanding' ? r.status === 'Requested' || r.status === 'Ordered' : r.status === filter)
      const searchMatch =
        !term || r.employeeName.toLowerCase().includes(term) || r.lines.some((l) => l.itemName.toLowerCase().includes(term))
      return statusMatch && searchMatch
    })
  }, [requests, filter, search])

  const changeStatus = async (request: UniformRequest, status: UniformStatus, reason?: string) => {
    setBusyId(request.uniformRequestId)
    try {
      const updated = await updateUniformRequestStatus(request.uniformRequestId, status, reason)
      setRequests((prev) => prev.map((r) => (r.uniformRequestId === updated.uniformRequestId ? updated : r)))
      showToast(`${request.employeeName}'s request marked ${status.toLowerCase()}`, 'success')
    } catch (error) {
      showToast(getApiErrorDetails(error, "The request couldn't be updated.").message, 'error')
    } finally {
      setBusyId(null)
    }
  }

  const countFor = (f: Filter) =>
    f === 'All'
      ? requests.length
      : f === 'Outstanding'
        ? requests.filter((r) => r.status === 'Requested' || r.status === 'Ordered').length
        : requests.filter((r) => r.status === f).length

  return (
    <section className='uni-card'>
      <div className='uni-toolbar'>
        <div className='uni-filters'>
          {FILTERS.map((f) => (
            <button
              key={f}
              type='button'
              className={`uni-filter${filter === f ? ' uni-filter--active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f} ({countFor(f)})
            </button>
          ))}
        </div>
        <Input
          className='uni-search'
          placeholder='Search by name or item'
          value={search}
          onChange={(e) => setSearch(String(e.value ?? ''))}
        />
      </div>

      {loading ? (
        <div className='uni-empty'>Loading requests…</div>
      ) : shown.length === 0 ? (
        <div className='uni-empty'>No requests here.</div>
      ) : (
        <table className='uni-table'>
          <thead>
            <tr>
              <th>Requested</th>
              <th>Employee</th>
              <th>Items</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.uniformRequestId}>
                <td>
                  {formatDate(r.requestedDate)}
                  {r.requestedByName && r.requestedByEmployeeId !== r.employeeId && (
                    <div className='uni-muted'>by {r.requestedByName}</div>
                  )}
                </td>
                <td>
                  {r.employeeName}
                  {r.notes && <div className='uni-muted'>“{r.notes}”</div>}
                </td>
                <td>
                  <RequestLines request={r} />
                </td>
                <td>
                  <StatusPill status={r.status} />{' '}
                  {r.outsideAllowance && <span className='uni-pill uni-pill--outside'>Outside allowance</span>}
                  {r.status === 'Ordered' && <div className='uni-muted'>Ordered {formatDate(r.orderedDate)}</div>}
                  {r.status === 'Delivered' && <div className='uni-muted'>Delivered {formatDate(r.deliveredDate)}</div>}
                  {(r.status === 'Declined' || r.status === 'Cancelled') && (
                    <div className='uni-muted'>
                      {formatDate(r.statusChangedDate)}
                      {r.statusChangedByName ? ` by ${r.statusChangedByName}` : ''}
                      {r.statusReason ? ` - ${r.statusReason}` : ''}
                    </div>
                  )}
                </td>
                <td>
                  <div className='uni-actions'>
                    {r.status === 'Requested' && (
                      <button
                        type='button'
                        className='uni-btn uni-btn--secondary uni-btn--small'
                        disabled={busyId === r.uniformRequestId}
                        onClick={() => changeStatus(r, 'Ordered')}
                      >
                        Mark ordered
                      </button>
                    )}
                    {(r.status === 'Requested' || r.status === 'Ordered') && (
                      <>
                        <button
                          type='button'
                          className='uni-btn uni-btn--primary uni-btn--small'
                          disabled={busyId === r.uniformRequestId}
                          onClick={() => changeStatus(r, 'Delivered')}
                        >
                          Mark delivered
                        </button>
                        <button
                          type='button'
                          className='uni-btn uni-btn--danger uni-btn--small'
                          disabled={busyId === r.uniformRequestId}
                          onClick={() => setReasonFor({ request: r, status: 'Declined' })}
                        >
                          Decline
                        </button>
                        <button
                          type='button'
                          className='uni-btn uni-btn--secondary uni-btn--small'
                          disabled={busyId === r.uniformRequestId}
                          onClick={() => setReasonFor({ request: r, status: 'Cancelled' })}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {reasonFor && (
        <ReasonDialog
          title={reasonFor.status === 'Declined' ? 'Decline request' : 'Cancel request'}
          request={reasonFor.request}
          onClose={() => setReasonFor(null)}
          onConfirm={(reason) => {
            const { request, status } = reasonFor
            setReasonFor(null)
            changeStatus(request, status, reason)
          }}
        />
      )}
    </section>
  )
}

const ReasonDialog = ({
  title,
  request,
  onClose,
  onConfirm,
}: {
  title: string
  request: UniformRequest
  onClose: () => void
  onConfirm: (reason: string) => void
}) => {
  const [reason, setReason] = useState('')
  return (
    <Dialog title={title} onClose={onClose} width={460}>
      <div className='uni-form uni-panel'>
        <div>
          {request.employeeName}, requested {formatDate(request.requestedDate)}:
          <RequestLines request={request} />
        </div>
        <div className='uni-field'>
          <label className='uni-field__label'>Reason (optional)</label>
          <TextArea value={reason} rows={3} maxLength={500} onChange={(e) => setReason(String(e.value ?? ''))} />
          <span className='uni-field__hint'>{request.employeeName} will see this in the app notification.</span>
        </div>
      </div>
      <DialogActionsBar>
        <button type='button' className='uni-btn uni-btn--secondary' onClick={onClose}>
          Back
        </button>
        <button type='button' className='uni-btn uni-btn--danger' onClick={() => onConfirm(reason)}>
          {title}
        </button>
      </DialogActionsBar>
    </Dialog>
  )
}

/**
 * Raises a request for someone. It is checked against their allowance, unless
 * it's outside the allowance - recorded, flagged, and never counted against it.
 */
const NewRequestDialog = ({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) => {
  const { showToast } = useToast()
  const [employees, setEmployees] = useState<dropDown[]>([])
  const [employee, setEmployee] = useState<dropDown | null>(null)
  const [summaries, setSummaries] = useState<UniformAllowanceSummary[]>([])
  const [quantities, setQuantities] = useState<Record<number, number>>({})
  const [sizes, setSizes] = useState<Record<number, string>>({})
  const [outsideAllowance, setOutsideAllowance] = useState(false)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const searchEmployees = async (term: string) => {
    try {
      setEmployees((await getEmployeeDropdown(term)).map((e) => ({ text: e.text, value: Number(e.value) })))
    } catch {
      setEmployees([])
    }
  }

  useEffect(() => {
    searchEmployees('')
  }, [])

  useEffect(() => {
    setSummaries([])
    setQuantities({})
    setSizes({})
    if (!employee?.value) return
    getEmployeeUniform(Number(employee.value))
      .then((u) => {
        setSummaries(u.items)
        setSizes(Object.fromEntries(u.items.map((i) => [i.uniformItemId, i.lastSize && i.sizes.includes(i.lastSize) ? i.lastSize : ''])))
      })
      .catch((err) => setError(getApiErrorDetails(err, "Their allowance couldn't be loaded.").message))
  }, [employee])

  const overAllowance = summaries.filter((s) => (quantities[s.uniformItemId] ?? 0) > s.available)
  const chosen = summaries.filter((s) => (quantities[s.uniformItemId] ?? 0) > 0)

  const submit = async () => {
    setError(null)
    if (!employee?.value) return setError('Choose who the request is for.')
    if (chosen.length === 0) return setError('Choose at least one item.')
    const missingSize = chosen.find((s) => s.sizes.length > 0 && !sizes[s.uniformItemId])
    if (missingSize) return setError(`Choose a size for the ${missingSize.name.toLowerCase()}.`)
    if (!outsideAllowance && overAllowance.length > 0) {
      return setError('Some items are over their allowance. Reduce them, or raise this outside their allowance.')
    }

    setSaving(true)
    try {
      const { emailError } = await createUniformRequestOnBehalf({
        employeeId: Number(employee.value),
        lines: chosen.map((s) => ({
          uniformItemId: s.uniformItemId,
          size: s.sizes.length > 0 ? sizes[s.uniformItemId] : null,
          quantity: quantities[s.uniformItemId],
        })),
        notes: notes.trim() || null,
        outsideAllowance,
      })
      showToast(
        emailError ? `Request raised, but not emailed: ${emailError}` : 'Request raised',
        emailError ? 'error' : 'success'
      )
      onCreated()
    } catch (err) {
      setError(getApiErrorDetails(err, "The request couldn't be raised.").message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog title='New uniform request' onClose={onClose} width={720}>
      <div className='uni-form uni-panel'>
        <div className='uni-field'>
          <label className='uni-field__label'>
            For <em>*</em>
          </label>
          <DropDownList
            data={employees}
            textField='text'
            dataItemKey='value'
            filterable={true}
            onFilterChange={(e: DropDownListFilterChangeEvent) => searchEmployees(e.filter.value ?? '')}
            value={employee}
            defaultItem={{ text: 'Choose an employee', value: 0 }}
            onChange={(e) => setEmployee(e.value?.value ? e.value : null)}
          />
        </div>

        {employee && summaries.length > 0 && (
          <table className='uni-table'>
            <thead>
              <tr>
                <th>Item</th>
                <th>Size</th>
                <th className='uni-num'>Left this year</th>
                <th className='uni-num'>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {summaries.map((s) => {
                const qty = quantities[s.uniformItemId] ?? 0
                return (
                  <tr key={s.uniformItemId}>
                    <td>{s.name}</td>
                    <td>
                      {s.sizes.length > 0 ? (
                        <DropDownList
                          data={s.sizes}
                          value={sizes[s.uniformItemId] || null}
                          defaultItem='Choose'
                          onChange={(e) =>
                            setSizes((prev) => ({ ...prev, [s.uniformItemId]: e.value === 'Choose' ? '' : e.value }))
                          }
                          style={{ width: '130px' }}
                        />
                      ) : (
                        <span className='uni-muted'>One size</span>
                      )}
                    </td>
                    <td className={`uni-num${s.available === 0 ? ' uni-left--none' : ''}`}>
                      {s.available} of {s.allowance}
                    </td>
                    <td className='uni-num'>
                      <NumericTextBox
                        value={qty}
                        min={0}
                        format='n0'
                        width={90}
                        onChange={(e) => setQuantities((prev) => ({ ...prev, [s.uniformItemId]: Math.max(0, e.value ?? 0) }))}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {employee && (
          <div className='uni-field'>
            <label className='uni-field__label'>Outside their allowance</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Switch checked={outsideAllowance} onChange={(e) => setOutsideAllowance(e.value)} />
              <span className='uni-field__hint'>
                Not checked against their allowance and never counted against it. Flagged in their history so repeat
                requests stand out.
              </span>
            </div>
          </div>
        )}

        {!outsideAllowance && overAllowance.length > 0 && (
          <div className='uni-warning'>
            Over allowance: {overAllowance.map((s) => s.name).join(', ')}. Reduce the quantity, or raise this outside
            their allowance.
          </div>
        )}

        {employee && (
          <div className='uni-field'>
            <label className='uni-field__label'>Notes</label>
            <TextArea value={notes} rows={2} maxLength={1000} onChange={(e) => setNotes(String(e.value ?? ''))} />
          </div>
        )}

        {error && <div className='uni-error'>{error}</div>}
      </div>
      <DialogActionsBar>
        <button type='button' className='uni-btn uni-btn--secondary' onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type='button' className='uni-btn uni-btn--accent' onClick={submit} disabled={saving || !employee}>
          {saving ? 'Raising…' : 'Raise request'}
        </button>
      </DialogActionsBar>
    </Dialog>
  )
}

// --- Items ------------------------------------------------------------------------

const ItemsTab = ({ refreshKey, onEdit }: { refreshKey: number; onEdit: (item: UniformItem) => void }) => {
  const { showToast } = useToast()
  const [items, setItems] = useState<UniformItem[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setItems(await getUniformItems(true))
    } catch (error) {
      showToast(getApiErrorDetails(error, "Uniform items couldn't be loaded.").message, 'error')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  const remove = async (item: UniformItem) => {
    if (!window.confirm(`Delete ${item.name}? Past requests for it keep their history.`)) return
    try {
      await deleteUniformItem(item.uniformItemId)
      setItems((prev) => prev.filter((i) => i.uniformItemId !== item.uniformItemId))
      showToast(`${item.name} deleted`, 'success')
    } catch (error) {
      showToast(getApiErrorDetails(error, "The item couldn't be deleted.").message, 'error')
    }
  }

  return (
    <section className='uni-card'>
      {loading ? (
        <div className='uni-empty'>Loading items…</div>
      ) : items.length === 0 ? (
        <div className='uni-empty'>No uniform items yet. Add the first one to get started.</div>
      ) : (
        <table className='uni-table'>
          <thead>
            <tr>
              <th>Item</th>
              <th>Sizes</th>
              <th className='uni-num'>Default allowance</th>
              <th>Offered to</th>
              <th className='uni-num'>Price</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.uniformItemId}>
                <td style={{ fontWeight: 600 }}>{item.name}</td>
                <td>
                  {item.sizes.length > 0 ? (
                    item.sizes.map((s) => (
                      <span key={s} className='uni-size-chip'>
                        {s}
                      </span>
                    ))
                  ) : (
                    <span className='uni-muted'>One size</span>
                  )}
                </td>
                <td className='uni-num'>{item.defaultAllowance} a year</td>
                <td>
                  {!item.limitedToGroups ? (
                    <span className='uni-muted'>Everyone</span>
                  ) : item.groups.length === 0 ? (
                    <span className='uni-muted'>Nobody - its groups were deleted</span>
                  ) : (
                    item.groups.map((g) => (
                      <span key={g.groupId} className='uni-size-chip'>
                        {g.name}
                      </span>
                    ))
                  )}
                </td>
                <td className='uni-num'>{item.unitPrice != null ? `£${item.unitPrice.toFixed(2)}` : '-'}</td>
                <td>{item.isActive ? <span className='uni-pill uni-pill--delivered'>Active</span> : <span className='uni-pill uni-pill--cancelled'>Hidden</span>}</td>
                <td>
                  <div className='uni-actions'>
                    <button type='button' className='uni-btn uni-btn--secondary uni-btn--small' onClick={() => onEdit(item)}>
                      Edit
                    </button>
                    <button type='button' className='uni-btn uni-btn--danger uni-btn--small' onClick={() => remove(item)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

const ItemDialog = ({ item, onClose, onSaved }: { item: UniformItem | null; onClose: () => void; onSaved: () => void }) => {
  const { showToast } = useToast()
  const [name, setName] = useState(item?.name ?? '')
  const [sizesText, setSizesText] = useState(item?.sizes.join(', ') ?? '')
  const [defaultAllowance, setDefaultAllowance] = useState<number>(item?.defaultAllowance ?? 0)
  const [unitPrice, setUnitPrice] = useState<number | null>(item?.unitPrice ?? null)
  const [isActive, setIsActive] = useState(item?.isActive ?? true)
  const [sortOrder, setSortOrder] = useState<number>(item?.sortOrder ?? 0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sizes = sizesText
    .split(',')
    .map((s) => s.trim())
    .filter((s, i, all) => s.length > 0 && all.findIndex((o) => o.toLowerCase() === s.toLowerCase()) === i)

  const save = async () => {
    setError(null)
    if (!name.trim()) return setError('Give the item a name.')
    setSaving(true)
    try {
      await saveUniformItem(item?.uniformItemId ?? null, {
        name: name.trim(),
        sizes,
        defaultAllowance,
        unitPrice,
        isActive,
        sortOrder,
      })
      showToast(item ? `${name.trim()} saved` : `${name.trim()} added`, 'success')
      onSaved()
    } catch (err) {
      setError(getApiErrorDetails(err, "The item couldn't be saved.").message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog title={item ? `Edit ${item.name}` : 'Add uniform item'} onClose={onClose} width={520}>
      <div className='uni-form uni-panel'>
        <div className='uni-field'>
          <label className='uni-field__label'>
            Name <em>*</em>
          </label>
          <Input value={name} maxLength={100} placeholder='e.g. T-shirt' onChange={(e) => setName(String(e.value ?? ''))} />
        </div>
        <div className='uni-field'>
          <label className='uni-field__label'>Sizes</label>
          <Input
            value={sizesText}
            placeholder='e.g. S, M, L, XL, XXL'
            onChange={(e) => setSizesText(String(e.value ?? ''))}
          />
          <span className='uni-field__hint'>
            Separate sizes with commas, in the order they should be offered. Leave empty for one size.
          </span>
          {sizes.length > 0 && (
            <div>
              {sizes.map((s) => (
                <span key={s} className='uni-size-chip'>
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className='uni-row'>
          <div className='uni-field'>
            <label className='uni-field__label'>Default allowance a year</label>
            <NumericTextBox value={defaultAllowance} min={0} format='n0' onChange={(e) => setDefaultAllowance(Math.max(0, e.value ?? 0))} />
          </div>
          <div className='uni-field'>
            <label className='uni-field__label'>Price each</label>
            <NumericTextBox value={unitPrice} min={0} format='c2' onChange={(e) => setUnitPrice(e.value ?? null)} />
          </div>
          <div className='uni-field'>
            <label className='uni-field__label'>Order in lists</label>
            <NumericTextBox value={sortOrder} format='n0' onChange={(e) => setSortOrder(e.value ?? 0)} />
          </div>
        </div>
        <div className='uni-field'>
          <label className='uni-field__label'>Available to request</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Switch checked={isActive} onChange={(e) => setIsActive(e.value)} />
            <span className='uni-field__hint'>Hidden items can't be requested but keep their history.</span>
          </div>
        </div>
        <span className='uni-field__hint'>
          Everyone it's offered to gets the default allowance. Choose who it's offered to on the Groups tab (no groups
          means everyone), and change a person's allowance on the Allocations tab.
        </span>
        {error && <div className='uni-error'>{error}</div>}
      </div>
      <DialogActionsBar>
        <button type='button' className='uni-btn uni-btn--secondary' onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type='button' className='uni-btn uni-btn--primary' onClick={save} disabled={saving}>
          {saving ? 'Saving…' : item ? 'Save' : 'Add item'}
        </button>
      </DialogActionsBar>
    </Dialog>
  )
}

export default UniformPage
